import React, { CSSProperties, HTMLAttributes } from 'react';
import clsx from 'clsx';
import Select, { ActionMeta, OptionTypeBase } from 'react-select';
import CreatableSelect from 'react-select/async-creatable';
import { emphasize, useTheme, Theme } from '@mui/material/styles';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import Typography from '@mui/material/Typography';
import NoSsr from '@mui/material/NoSsr';
import TextField, { BaseTextFieldProps } from '@mui/material/TextField';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import MenuItem from '@mui/material/MenuItem';
import CancelIcon from '@mui/icons-material/Cancel';
import { ValueContainerProps } from 'react-select/src/components/containers';
import { ControlProps } from 'react-select/src/components/Control';
import { MenuProps, NoticeProps } from 'react-select/src/components/Menu';
import { MultiValueProps } from 'react-select/src/components/MultiValue';
import { OptionProps } from 'react-select/src/components/Option';
import { PlaceholderProps } from 'react-select/src/components/Placeholder';
import { SingleValueProps } from 'react-select/src/components/SingleValue';
import { ValueType } from 'react-select/src/types';
import { DistributiveOmit } from '@mui/types';
import http from '../../utils/http';

interface OptionType extends OptionTypeBase {
  label: string;
  value: string | number;
  leftSlot?: React.ComponentType | null;
  rightSlot?: React.ComponentType | null;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      flexGrow: 1,
      minWidth: 290,
    },
    input: {
      display: 'flex',
      padding: theme.spacing(1),
      height: 'auto',
    },
    valueContainer: {
      display: 'flex',
      flexWrap: 'wrap',
      flex: 1,
      alignItems: 'center',
      overflow: 'hidden',
    },
    chip: {
      margin: theme.spacing(0.5, 0.25),
    },
    chipFocused: {
      backgroundColor: emphasize(
        theme.palette.mode === 'light'
          ? theme.palette.grey[300]
          : theme.palette.grey[700],
        0.08,
      ),
    },
    noOptionsMessage: {
      padding: theme.spacing(1, 2),
    },
    singleValue: {
      fontSize: 16,
    },
    placeholder: {
      fontSize: 16,
    },
    paper: {
      margin: theme.spacing(1, 0, 1),
      border: `1px solid ${theme.palette.divider}`,
    },
  }),
);

function NoOptionsMessage(
  props: React.PropsWithChildren<NoticeProps<OptionType>>,
) {
  return (
    <Typography
      color="textSecondary"
      className={props.selectProps.classes.noOptionsMessage}
      {...props.innerProps}
    >
      {props.children}
    </Typography>
  );
}

type InputComponentProps = Pick<BaseTextFieldProps, 'inputRef'> &
  HTMLAttributes<HTMLDivElement>;

function inputComponent({ inputRef, ...props }: InputComponentProps) {
  return <div ref={inputRef} {...props} />;
}

function Control(props: ControlProps<OptionType>) {
  const {
    children,
    innerProps,
    innerRef,
    selectProps: { classes, TextFieldProps },
  } = props;

  return (
    <TextField
      fullWidth
      InputProps={{
        inputComponent,
        inputProps: {
          className: classes.input,
          ref: innerRef,
          children,
          ...innerProps,
        },
      }}
      {...TextFieldProps}
    />
  );
}

function Option(props: OptionProps<OptionType>) {
  const {
    leftSlot: Left = null,
    rightSlot: Right = null,
    ...rest
  } = props.data;

  return (
    <MenuItem
      ref={props.innerRef}
      selected={props.isFocused}
      component={'div' as any}
      style={{
        position: 'relative',
        fontWeight: props.isSelected ? 500 : 400,
      }}
      {...props.innerProps}
    >
      {Left && <Left {...rest} />}
      {props.children}
      {Right && <Right {...rest} />}
    </MenuItem>
  );
}

type MuiPlaceholderProps = DistributiveOmit<
  PlaceholderProps<OptionType>,
  'innerProps'
> &
  Partial<Pick<PlaceholderProps<OptionType>, 'innerProps'>>;
function Placeholder(props: React.PropsWithChildren<MuiPlaceholderProps>) {
  const { selectProps, innerProps = {}, children } = props;
  return (
    <Typography
      color="textSecondary"
      className={selectProps.classes.placeholder}
      {...innerProps}
    >
      {children}
    </Typography>
  );
}

function SingleValue(props: SingleValueProps<OptionType>) {
  return (
    <Typography
      className={props.selectProps.classes.singleValue}
      {...props.innerProps}
    >
      {props.children}
    </Typography>
  );
}

function ValueContainer(
  props: React.PropsWithChildren<ValueContainerProps<OptionType>>,
) {
  return (
    <div className={props.selectProps.classes.valueContainer}>
      {props.children}
    </div>
  );
}

function MultiValue(
  props: React.PropsWithChildren<MultiValueProps<OptionType>>,
) {
  return (
    <Chip
      tabIndex={-1}
      label={props.children}
      className={clsx(props.selectProps.classes.chip, {
        [props.selectProps.classes.chipFocused]: props.isFocused,
      })}
      onDelete={props.removeProps.onClick}
      deleteIcon={<CancelIcon {...props.removeProps} />}
    />
  );
}

function Menu(props: React.PropsWithChildren<MenuProps<OptionType>>) {
  return (
    <Paper
      square
      className={props.selectProps.classes.paper}
      {...props.innerProps}
    >
      {props.children}
    </Paper>
  );
}

const components = {
  Control,
  Menu,
  MultiValue,
  NoOptionsMessage,
  Option,
  Placeholder,
  SingleValue,
  ValueContainer,
};

interface Props {
  label?: string;
  placeholder?: string;
  noOptionsMessage?(): string;
  options: OptionType[];
  value: ValueType<OptionType>;
  onChange: (value: ValueType<OptionType>, actionMeta: ActionMeta) => void;
  multiple?: boolean;
  creatable?: boolean;
  choicesUrl?: string;
}

export default function DefaultSelect({
  label,
  placeholder,
  value,
  onChange,
  noOptionsMessage,
  options,
  multiple,
  creatable = false,
  choicesUrl,
}: Props) {
  const classes = useStyles();
  const theme = useTheme();

  const selectStyles = {
    input: (base: CSSProperties) => ({
      ...base,
      color: theme.palette.text.primary,
      '& input': {
        font: 'inherit',
      },
    }),
  };

  const Component = creatable
    ? CreatableSelect
    : (Select as React.ComponentType);

  const extraProps = React.useMemo<any>(
    () =>
      creatable && choicesUrl
        ? {
            defaultOptions: true,
            cacheOptions: true,
            loadOptions: (query: string) => {
              return http.get(choicesUrl, {
                params: {
                  query,
                },
              });
            },
          }
        : {
            loadOptions: () => options,
          },
    [creatable, options],
  );

  return (
    <div className={classes.root}>
      <NoSsr>
        {/* @ts-ignore */}
        <Component
          {...extraProps}
          isMulti={multiple}
          classes={classes}
          styles={selectStyles}
          TextFieldProps={{
            label,
            InputLabelProps: {
              shrink: true,
            },
          }}
          placeholder={placeholder}
          options={options}
          components={components}
          value={value}
          onChange={onChange}
          noOptionsMessage={noOptionsMessage}
        />
      </NoSsr>
    </div>
  );
}
